import type { PublicScheduleResponse } from '@crm-dance/public-api';

/**
 * Hand-rolled fetch wrapper for the embed bundle.
 *
 * <p>This file <em>deliberately</em> does not use {@code @crm-dance/public-api}
 * at runtime — that package depends on axios, which would balloon the
 * bundle past its 35 KB gzipped budget. We import the response type via
 * {@code import type} (zero runtime cost; Vite tree-shakes types) so type
 * drift is still caught at build time.
 *
 * <p>The endpoint, CORS rule, and rate-limit interceptor live in the
 * Spring backend; see {@code backend/.../PublicBookingController.java}
 * and {@code backend/.../BookingRateLimitInterceptor.java}.
 *
 * @see docs/specs/schedule-widget-embed/technical.md § API integration
 */

// Build-time default; override with `CRM_API_URL=… pnpm build` for staging
// or local-backend builds. The Vite config wires this through `define`.
declare const __CRM_API_URL__: string;
const API_URL = typeof __CRM_API_URL__ === 'string' ? __CRM_API_URL__ : 'https://api.crm.dance/api/v1';

/**
 * Categorised fetch failure. Lets {@code ScheduleList} branch on
 * not-found vs. server vs. network and render the right empty/error state.
 */
export class ApiError extends Error {
	constructor(
		public kind: 'not-found' | 'server' | 'network',
		public status?: number,
	) {
		super(`${kind}${status !== undefined ? ` (${status})` : ''}`);
		this.name = 'ApiError';
	}
}

/**
 * Fetch the public schedule for a studio.
 *
 * <p>No {@code from}/{@code to} query params are sent. The backend defaults
 * the window to "today + 1 month" in studio timezone — see
 * {@code PublicBookingController:69-70}. Sending UTC dates from the
 * visitor's browser would silently misalign for studios in non-UTC zones
 * (a Sydney studio's Friday classes would show as "yesterday" to a New
 * York visitor at the wrong hour).
 *
 * @param slug   the studio's URL slug, already validated against
 *               {@code ^[a-z0-9-]{2,64}$} by the caller
 * @param signal an {@link AbortSignal} from an {@link AbortController} that
 *               the caller controls; aborted on component unmount or when
 *               the slug changes mid-flight
 * @param locationId the embed's {@code data-location}: only that location's classes
 * @throws ApiError with kind:
 *   - {@code 'not-found'} for HTTP 404 (studio doesn't exist or was renamed)
 *   - {@code 'server'} for any other non-2xx
 *   - {@code 'network'} for any thrown error other than {@code AbortError}
 */
export async function getSchedule(
	slug: string,
	signal: AbortSignal,
	locationId?: string,
): Promise<PublicScheduleResponse> {
	const query = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
	const url = `${API_URL}/p/${encodeURIComponent(slug)}/schedule${query}`;
	let r: Response;
	try {
		r = await fetch(url, { method: 'GET', mode: 'cors', signal });
	} catch (e) {
		// Re-throw AbortError unchanged so the caller can ignore it on unmount.
		if ((e as Error).name === 'AbortError') throw e;
		throw new ApiError('network');
	}
	if (r.status === 404) throw new ApiError('not-found', 404);
	if (!r.ok) throw new ApiError('server', r.status);
	return (await r.json()) as PublicScheduleResponse;
}
